import { Component, OnInit } from "@angular/core";
import { Tab } from "src/app/common/interfaces/tab";
import { TabChange } from "src/app/common/interfaces/tab-change";
import { TabsComponent } from "../../../common/components/core/tabs/tabs.component";
import { CommonModule } from "@angular/common";
import { chartConfig } from "./../../../common/config/chart-config";
import { StorageService } from "src/app/common/services/storage.service";
import { LandingClientService } from "src/app/common/services/landing-page.client";
import { KPIDetails } from "src/app/common/interfaces/home/KPI-details.interface";
import { distinctUntilChanged, Subscription } from "rxjs";
import { ViewTypeService } from "src/app/services/view-type.service";
import { ViewType } from "src/app/common/enums/view-type.enum";
declare const FusionCharts: any;

interface DataPoint {
  label: string;
  value: string;
  showLabel?: string;
  showValue?: string;
  drawAnchors?: string;
  showToolTip?: string;
  showVerticalLine?: string;
  toolText?: string;
}

@Component({
  selector: "app-kpi",
  standalone: true,
  imports: [TabsComponent, CommonModule],
  templateUrl: "./kpi.component.html",
  styleUrls: ["./kpi.component.scss"],
})
export class KPIComponent implements OnInit {
  private viewTypeSubscription: Subscription | null = null;
  tabIndex = 0;
  defaultSubTabs: Tab[] = [
    { name: "Last 4 Weeks" },
    { name: "Last Week" },
    { name: "Current Week" },
  ];
  specialSubTabs: Tab[] = [
    { name: "Last 3 Months" },
    { name: "Last Month" },
    { name: "Last Month to Date" },
    { name: "Last Week to Date" },
  ];
  subTabs: Tab[] = [...this.defaultSubTabs];
  subTabIndex = 0;
  showCustomLegend: Boolean = true;
  showTrendline: Boolean = true;
  dataSources!: { [key: string]: any };
  currentDataSource: any;
  kpiDetails: KPIDetails | null = null;
  selectedViewType: ViewType = ViewType.DOR_VIEW;

  private readonly tabMetricMap: { [key: string]: string } = {
    Productivity: "productivity",
    Efficiency: "efficiency",
    "Mode of Tx": "modeOfTx",
    CPM: "cpm",
    PRN: "prn",
    Overtime: "overtime",
  };

  tabs: Tab[] = Object.keys(this.tabMetricMap).map((name) => ({ name }));

  private facilitySubscription: Subscription | null = null;
  constructor(
    private storageService: StorageService,
    private landingClientService: LandingClientService,
    private viewTypeService: ViewTypeService,
  ) {}

  ngOnInit() {
    this.viewTypeSubscription =
          this.viewTypeService.selectedViewType$.subscribe((viewType) => {
            this.selectedViewType = ViewType.NONE === viewType || ViewType.THERAPIST_VIEW === viewType ? ViewType.THERAPIST_VIEW : ViewType.DOR_VIEW;
            if(viewType !== ViewType.NONE){
              this.fetchKpiData();
            }
          });
    this.facilitySubscription =
      this.storageService.isSelectedFacility
      .pipe(
        distinctUntilChanged(),
      ).subscribe((facilityId) => {
        this.fetchKpiData(facilityId);
      });
  }

  ngOnDestroy() {
    this.facilitySubscription?.unsubscribe();
    this.viewTypeSubscription?.unsubscribe();
  }

  calculateDataPoints(): any {
    const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const originalData: DataPoint[] = [];

    type KpiMetric =
      | "productivity"
      | "efficiency"
      | "mode of tx"
      | "cpm"
      | "prn"
      | "overtime";

    const selectedMetric = this.tabs[
      this.tabIndex
    ].name.toLowerCase() as KpiMetric;
    var metricData =
      selectedMetric === "mode of tx"
        ? this.kpiDetails?.["modeOfTx"]
        : this.kpiDetails?.[selectedMetric];
    if (!metricData) return [];
    if (selectedMetric === "mode of tx") {
      const modeOfTxData = metricData;
      const categories: { category: { label: string }[] } = { category: [] };
      const dataset: {
        seriesname: string;
        color: string;
        data: any[];
      }[] = [
        { seriesname: "Concurrent", color: "#3974E0", data: [] },
        { seriesname: "Group", color: "#02BB9B", data: [] },
        { seriesname: "Individual", color: "#9F0A9F", data: [] },
      ];

      if (this.subTabIndex === 0) {
        Object.keys(modeOfTxData.last4Weeks).forEach((week) => {
          categories.category.push({ label: week });
          const weekData = modeOfTxData.last4Weeks[week];
          dataset[0].data.push({
            value: weekData.concurrentPercent.toString(),
            toolText: `Concurrent: ${weekData.concurrentPercent}%`,
          });
          dataset[1].data.push({
            value: weekData.groupPercent.toString(),
            toolText: `Group: ${weekData.groupPercent}%`,
          });
          dataset[2].data.push({
            value: weekData.individualPercent.toString(),
            toolText: `Individual: ${weekData.individualPercent}%`,
          });
        });
        if (Object.keys(modeOfTxData.last4Weeks).length === 3) {
          categories.category.push({ label: "week4" });
        }
      } else if (this.subTabIndex === 1) {
        const lastWeekData = modeOfTxData.lastWeek;
        Object.keys(lastWeekData).forEach((day) => {
          const weekData = lastWeekData[day];
          categories.category.push({ label: day });
          dataset[0].data.push({
            value: weekData.concurrentPercent.toString(),
            toolText: `Concurrent: ${weekData.concurrentPercent}%`,
          });
          dataset[1].data.push({
            value: weekData.groupPercent.toString(),
            toolText: `Group: ${weekData.groupPercent}%`,
          });
          dataset[2].data.push({
            value: weekData.individualPercent.toString(),
            toolText: `Individual: ${weekData.individualPercent}%`,
          });
        });
      } else if (this.subTabIndex === 2) {
        const currentWeekData = modeOfTxData.currentWeek ?? {};
        let lastValidIndex = -1;

        weekDays.forEach((day, index) => {
          if (currentWeekData[day]?.concurrentPercent !== undefined) {
            lastValidIndex = index;
          }
        });

        weekDays.forEach((day, index) => {
          const weekData = currentWeekData[day] ?? {
            concurrentPercent: 0,
            groupPercent: 0,
            individualPercent: 0,
          };

          categories.category.push({ label: day });

          dataset[0].data.push({
            value:
              index <= lastValidIndex
                ? weekData.concurrentPercent.toString()
                : "",
            toolText:
              index <= lastValidIndex
                ? `Concurrent: ${weekData.concurrentPercent}%`
                : "No data",
          });
          dataset[1].data.push({
            value:
              index <= lastValidIndex ? weekData.groupPercent.toString() : "",
            toolText:
              index <= lastValidIndex
                ? `Group: ${weekData.groupPercent}%`
                : "No data",
          });
          dataset[2].data.push({
            value:
              index <= lastValidIndex
                ? weekData.individualPercent.toString()
                : "",
            toolText:
              index <= lastValidIndex
                ? `Individual: ${weekData.individualPercent}%`
                : "No data",
          });
        });
      }

      return {
        ...chartConfig,
        modeOfTx: {
          ...chartConfig.modeOfTx,
          categories: [categories],
          dataset: dataset,
        },
      };
    }
    if (this.subTabIndex === 1) {
      const lastWeekData = metricData.lastWeek;
      Object.keys(lastWeekData).forEach((day) => {
        originalData.push({
          label: day,
          value: lastWeekData[day].toString(),
          showLabel: "1",
          showValue: "0",
          drawAnchors: "1",
          showVerticalLine: "1",
          showToolTip: "1",
          toolText: `${lastWeekData[day]}%`,
        });
      });
    } else if (this.subTabIndex === 0) {
      const last4WeeksData = metricData.last4Weeks;
      Object.keys(last4WeeksData).forEach((week) => {
        originalData.push({
          label: week,
          value: last4WeeksData[week].toString(),
          showLabel: "1",
          showValue: "0",
          drawAnchors: "1",
          showVerticalLine: "1",
          toolText: `${last4WeeksData[week]}%`,
        });
      });
      if (Object.keys(last4WeeksData).length === 3) {
        originalData.push({
          label: "week4",
          value: "",
          showLabel: "1",
          showValue: "0",
          drawAnchors: "0",
          showVerticalLine: "0",
        });
      }
    }
    if (this.subTabIndex === 2) {
      const currentWeekData = metricData.currentWeek ?? {};
      let lastValidIndex = -1;

      for (let i = 0; i < weekDays.length; i++) {
        if (currentWeekData[weekDays[i]] !== undefined) {
          lastValidIndex = i;
        }
      }

      for (let i = 0; i < weekDays.length; i++) {
        const day = weekDays[i];
        const value = currentWeekData[day];

        originalData.push({
          label: day,
          value: i <= lastValidIndex ? (value ?? "0").toString() : "",
          showLabel: "1",
          showToolTip: value !== undefined ? "1" : "0",
          drawAnchors: value !== undefined ? "1" : "0",
          showVerticalLine: "0",
          toolText: value !== undefined ? `${value}%` : "No data",
        });
      }
    }

    if (!originalData.length) {
      return [];
    }

    return {
      ...chartConfig,
      productivity: {
        ...chartConfig.productivity,
        data: originalData,
      },
    };
  }

  tabChange(tab: TabChange) {
    this.tabIndex = tab.to;
    const selectedTabName = this.tabs[this.tabIndex].name;
    if (
      selectedTabName === "CPM" ||
      selectedTabName === "PRN" ||
      selectedTabName === "Overtime"
    ) {
      this.subTabs = [...this.specialSubTabs];
    } else {
      this.subTabs = [...this.defaultSubTabs];
    }
    this.fetchKpiData();
    this.updateChart();
  }

  subTabChange(tab: TabChange) {
    this.subTabIndex = tab.to;
    this.dataSources = this.calculateDataPoints();
    this.updateChart();
  }

  updateChart() {
    const selectedTab = this.tabs[this.tabIndex].name;
    if (selectedTab === "Mode of Tx") {
      this.currentDataSource = this.dataSources["modeOfTx"];
      this.showCustomLegend = false;
    } else {
      this.currentDataSource = this.dataSources["productivity"];
      this.showCustomLegend = true;
    }

    FusionCharts.ready(() => {
      new FusionCharts({
        type: selectedTab === "Mode of Tx" ? "stackedcolumn2d" : "spline",
        renderAt: "chart-container",
        width: "100%",
        height: "300",
        dataFormat: "json",
        dataSource: this.currentDataSource,
      }).render();
    });
  }

  toggleTrendline() {
    this.showTrendline = !this.showTrendline;

    if (this.showTrendline) {
      this.dataSources["productivity"].trendlines = [
        {
          line: [
            {
              startvalue: "50",
              color: "#0000FF",
              displayValue: " ",
              dashed: "1",
              dashLen: "4",
              dashGap: "2",
              valueOnRight: "1",
              showOnTop: "1",
            },
          ],
        },
      ];
    } else {
      delete this.dataSources["productivity"].trendlines;
    }

    this.updateChart();
  }

  fetchKpiData(selectedFacilityId?: string) {
    const facilityId = selectedFacilityId
      ? selectedFacilityId
      : this.storageService.getSelectedFacilityId();
    const selectedTab = this.tabs[this.tabIndex].name;
    const metric = this.getMetricByTab(selectedTab);
    if (!facilityId || !metric) return;

    this.landingClientService
      .getKPIDetails(facilityId, this.selectedViewType)
      .subscribe((details: KPIDetails) => {
        this.kpiDetails = details;
        this.dataSources = this.calculateDataPoints();
        this.updateChart();
      });
  }

  private getMetricByTab(tabName: string): string {
    const productivityTabs = ["Productivity", "Mode of Tx", "Efficiency"];
    return productivityTabs.includes(tabName) ? "Productivity" : "CPM";
  }
}
